import { useState, useEffect } from 'react';
import {
  Modal,
  Fade,
  Backdrop,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import { Close, FileCopyOutlined, Facebook, Share, Twitter, WhatsApp } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReferralCode, fetchReferralStats } from '../../actions';
import { trackReferralFunnel } from '../../utils/tracking';
import { isInstitutionalPromotionEnabled } from '../institutional/institutional-flags';
import InstitutionalReferralProgress from '../institutional/InstitutionalReferralProgress';
import { daysOf, hasPaidSubscription, referralRewards } from '../../utils/referral-rewards';
import { REPLAY_MASK } from "../../utils/replay-mask";
import { referralRewardsAvailable } from "../../utils/native-referral-policy";
import {
  INVITE_CAMPAIGN,
  INVITE_CHANNEL,
  inviteDisplayLink,
  withInviteUtm,
} from "../../utils/invite-link";

const buildFallbackShareLink = (referralCode) => {
  if (!referralCode) return null;
  const origin = window.location.origin || '';
  return `${origin}/login?ref=${encodeURIComponent(referralCode)}`;
};

// Title and body for the reward state, as plain branches (no nested ternaries).
// "Free Pro" titles need the INVITER's reward: when only the friend is rewarded
// there is nothing to earn.
const shareModalTitle = ({ gift, inviterDays }) => {
  if (gift) return 'Invite a friend to QLearn';
  if (inviterDays !== null) return 'Share & Earn Free Pro';
  return 'Share QLearn';
};

const BODY_STYLE = { marginBottom: 8, color: 'var(--sub-text, rgba(0,0,0,0.54))' };

const ShareModalBody = ({ gift, inviterDays, friendDays }) => {
  if (gift && friendDays !== null) {
    return (
      <Typography variant="body2" style={BODY_STYLE}>
        Give friends <strong>{daysOf(friendDays)} of free Pro</strong> when they join with your link.
      </Typography>
    );
  }
  if (!gift && (inviterDays !== null || friendDays !== null)) {
    return (
      <Typography variant="body2" style={BODY_STYLE}>
        {friendDays !== null && (
          <>Give friends <strong>{daysOf(friendDays)} of Pro</strong> when they join with your link.</>
        )}
        {friendDays !== null && inviterDays !== null && ' '}
        {inviterDays !== null && (
          <>When they finish their first quiz, you get <strong>{daysOf(inviterDays)} of Pro free</strong>.</>
        )}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" style={BODY_STYLE}>
      Invite friends who are preparing for their exams to join you on QLearn.
    </Typography>
  );
};

// `absite`: the invite prompt passes it for ABSITE-only quizzes and the ABSITE
// diagnostic, so the friend's message names the exam. Other callers do not know
// the exam, and get the board-prep wording.
// `framing`: 'gift' for paying students, whose own referral reward would run
// alongside time they already pay for, so only the friend's reward is offered;
// 'earn' otherwise. The invite prompt passes its decision. Other openers (menu,
// qlink, create-quiz) pass nothing, and the modal uses subscriptions already in
// the store if there are any (it never fetches them); with none loaded it falls
// back to 'earn', as before.
// `campaign`: the opener's INVITE_CAMPAIGN slug (utils/invite-link.js), the
// utm_campaign of every link shared from here; each button adds its own
// channel. An opener that passes none shares as INVITE_CAMPAIGN.UNSPECIFIED.
// The institutional campaign's link always shares as
// INVITE_CAMPAIGN.INSTITUTIONAL instead, whoever opened the window.
const ShareEarnModalContent = ({ open, onClose, absite = false, framing, campaign }) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  // Dark institutional campaign (REACT_APP_INSTITUTIONAL_PROMOTION_ENABLED):
  // when its progress endpoint returns a referral link, share that one so
  // referred signups are attributed to the campaign.
  const institutionalPromotion = isInstitutionalPromotionEnabled();
  const [campaignLink, setCampaignLink] = useState(null);

  const referralState = useSelector((state) => state.referral);
  const subscriptions = useSelector((state) => state.getActivePlans?.plansList);
  const gift = (framing || (hasPaidSubscription(subscriptions) ? 'gift' : 'earn')) === 'gift';
  const referralPayload = referralState?.referralCode;
  const referralCode = referralPayload?.referralCode;
  const sharesCampaignLink = Boolean(institutionalPromotion && campaignLink);
  const shareLink = sharesCampaignLink
    ? campaignLink
    : (referralPayload?.referralLink || buildFallbackShareLink(referralCode));
  const stats = referralState?.stats;
  const loading = referralState?.loading;
  const pendingRewardReferrals = stats?.pendingRewardReferrals || 0;
  const rewardedReferrals = stats?.totalReferrals || 0;
  const totalJoined = rewardedReferrals + pendingRewardReferrals;
  const rewardPlanConfigured = stats?.rewardPlanConfigured;
  // Reward durations come from backend config, with no fallback: while the stats
  // load, or unless rewardPlanConfigured and referralEnabled are both true,
  // nothing is said about days or free Pro (rules in utils/referral-rewards.js,
  // shared with the invite prompt).
  const { inviterDays: rewardDays, friendDays: refereeRewardDays } = referralRewards(stats);

  // The link each button shares: ref as the server (or the fallback) gave it,
  // plus the invite utm tags for this opener and that button.
  const linkCampaign = sharesCampaignLink ? INVITE_CAMPAIGN.INSTITUTIONAL : campaign;
  const linkFor = (channel) => withInviteUtm(shareLink, { campaign: linkCampaign, channel });
  const copyLink = linkFor(INVITE_CHANNEL.COPY);
  // The chip shows the short form; copying from it by hand still copies the
  // tagged link, as the copy button does.
  const displayLink = inviteDisplayLink(copyLink);
  const handleChipCopy = (event) => {
    if (!copyLink || !event.clipboardData) return;
    event.preventDefault();
    event.clipboardData.setData('text/plain', copyLink);
  };
  const shareMessageWith = (link) => `I've been using QLearn for ${absite ? 'ABSITE' : 'board'} prep. `
    + (refereeRewardDays !== null
      ? `Sign up with my link and you get ${daysOf(refereeRewardDays)} of free Pro: ${link}`
      : `Sign up with my link: ${link}`);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    if (open) {
      trackReferralFunnel('referral_modal_opened');
    }
  }, [open]);

  useEffect(() => {
    if (open && !referralPayload) {
      dispatch(fetchReferralCode());
    }
    if (open && !stats) {
      dispatch(fetchReferralStats());
    }
  }, [open, referralPayload, stats, dispatch]);

  const handleCopy = async () => {
    if (!shareLink) return;
    await navigator.clipboard.writeText(copyLink);
    trackReferralFunnel('referral_link_copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleNativeShare = async () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'native' });
    try {
      // The link goes in `url` only: share targets add it to the text
      // themselves, so putting it in both sent the friend the link twice.
      await navigator.share({
        title: 'QLearn',
        text: shareMessageWith('').trimEnd(),
        url: linkFor(INVITE_CHANNEL.NATIVE),
      });
    } catch (shareError) {
      // Dismissing the share sheet rejects with AbortError; nothing to recover.
      console.debug('Share sheet closed:', shareError?.message);
    }
  };

  const handleWhatsApp = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'whatsapp' });
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessageWith(linkFor(INVITE_CHANNEL.WHATSAPP)))}`, '_blank');
  };

  const handleFacebook = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'facebook' });
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(linkFor(INVITE_CHANNEL.FACEBOOK))}`, '_blank');
  };

  const handleTwitter = () => {
    trackReferralFunnel('referral_share_clicked', { channel: 'twitter' });
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessageWith(linkFor(INVITE_CHANNEL.TWITTER)))}`, '_blank');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          // Theme surface + text via the theme CSS vars (set on document.body by
          // setTheme) so this MUI portal isn't a stark white box in dark mode.
          // Light mode stays visually near-identical (surface #fff / text #000),
          // aside from the chip + secondary text picking up the app tokens.
          background: 'var(--background-color, #fff)',
          color: 'var(--text-color, #000)',
          border: '1px solid var(--border-color, transparent)',
          borderRadius: 8, padding: 24,
          minWidth: 320, maxWidth: 480, outline: 'none'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            {/* B9: no "Free Pro" framing unless a reward is confirmed on with a
                positive day count, so we never promise a reward we can't grant.
                The share UI (and, when rewardPlanConfigured is false, the
                "temporarily unavailable" notice) still render. */}
            <Typography variant="h6">
              {shareModalTitle({ gift, inviterDays: rewardDays })}
            </Typography>
            <IconButton size="small" aria-label="Close" onClick={onClose} style={{ color: 'var(--action-icon, rgba(0,0,0,0.54))' }}><Close fontSize="small" /></IconButton>
          </Box>

          <ShareModalBody gift={gift} inviterDays={rewardDays} friendDays={refereeRewardDays} />

          {totalJoined > 0 && (
            <Typography variant="caption" style={{ display: 'block', marginBottom: 12, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              {totalJoined} {totalJoined === 1 ? 'person' : 'people'} joined via your link
            </Typography>
          )}
          {/* The pending count is the inviter's own reward, so not in gift framing. */}
          {!gift && pendingRewardReferrals > 0 && (
            <Typography variant="caption" style={{ display: 'block', marginBottom: 12, color: 'var(--sub-text, rgba(0,0,0,0.54))' }}>
              {pendingRewardReferrals} {pendingRewardReferrals === 1 ? 'reward unlocks' : 'rewards unlock'} when your friends finish their first quiz
            </Typography>
          )}
          {rewardPlanConfigured === false && (
            <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 12, color: 'var(--error-text, #c62828)' }}>
              Pro rewards are temporarily unavailable.
            </Typography>
          )}

          {institutionalPromotion && open && (
            <Box style={{ marginBottom: 16 }}>
              <InstitutionalReferralProgress onReferralLink={setCampaignLink} />
            </Box>
          )}

          {loading && !shareLink ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : shareLink ? (
            <>
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 8,
                // themed light-grey link chip -> readable in dark (list-bkg token)
                background: 'var(--list-bkg, #f5f5f5)', borderRadius: 4, padding: '6px 10px', marginBottom: 16
              }}>
                {/* Masked (M3): the link carries this student's referral code,
                    which identifies their account. */}
                <Typography variant="caption" onCopy={handleChipCopy} style={{ flex: 1, wordBreak: 'break-all' }} {...REPLAY_MASK}>
                  {displayLink}
                </Typography>
                {/* On the raised link chip (--list-bkg), the de-emphasized
                    --action-icon grey is only ~1.9:1 in dark. Use the chip's own
                    text color (matches the link text beside it) for an AA-legible,
                    tappable copy affordance in both themes. */}
                <IconButton size="small" aria-label="Copy link" onClick={handleCopy} style={{ color: 'var(--text-color, rgba(0,0,0,0.87))' }}>
                  <FileCopyOutlined fontSize="small" />
                </IconButton>
              </Box>
              {copied && (
                <Typography variant="caption" color="primary" style={{ display: 'block', marginBottom: 8, color: 'var(--list-item-sel-color, #3f51b5)' }}>
                  Copied!
                </Typography>
              )}
              <Box display="flex" gap={8} style={{ gap: 8, marginBottom: 8 }}>
                <Button
                  variant="contained"
                  style={{ background: '#25D366', color: '#fff', flex: 1 }}
                  startIcon={<WhatsApp />}
                  onClick={handleWhatsApp}
                >
                  WhatsApp
                </Button>
                {canNativeShare && (
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ flex: 1 }}
                    startIcon={<Share />}
                    onClick={handleNativeShare}
                  >
                    Share…
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={8} style={{ gap: 8 }}>
                <Button
                  variant="contained"
                  style={{ background: '#1877F2', color: '#fff', flex: 1 }}
                  startIcon={<Facebook />}
                  onClick={handleFacebook}
                >
                  Facebook
                </Button>
                <Button
                  variant="contained"
                  style={{ background: '#1DA1F2', color: '#fff', flex: 1 }}
                  startIcon={<Twitter />}
                  onClick={handleTwitter}
                >
                  Twitter / X
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="error" style={{ color: 'var(--error-text, #c62828)' }}>Not able to Fetch Referral link</Typography>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

// iOS ruling B5: the native app never shows Share & Earn, whoever opens it.
const ShareEarnModal = (props) => (referralRewardsAvailable() ? <ShareEarnModalContent {...props} /> : null);

export default ShareEarnModal;
